import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Loader2, RefreshCw } from 'lucide-react';
import type { Team, Contact, ContactFilters, TeamMember } from '../../types';
import { getContacts } from '../../services/contacts';
import { getTeamMembers } from '../../services/teams';
import LeadFilters from './LeadFilters';
import LeadTable from './LeadTable';
import LeadDrawer from './LeadDrawer';

interface LeadsPageProps {
  team: Team;
  userId: string;
  isAdmin?: boolean;
}

const LeadsPage: React.FC<LeadsPageProps> = ({ team, userId, isAdmin }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ContactFilters>({
    owner: '',
    type: '',
    status: '',
    followUp: '',
    segment: '',
    needsEmail: false,
    query: '',
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [contactsData, membersData] = await Promise.all([
      getContacts(team.id, { allTeams: isAdmin }),
      getTeamMembers(team.id),
    ]);
    setContacts(contactsData);
    setMembers(membersData);
    setLoading(false);
  }, [team.id, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (filters.query) {
        const haystack = [
          c.full_name,
          c.first_name,
          c.last_name,
          c.company,
          c.email,
          c.title,
          c.linkedin_url,
          c.location,
          c.country,
          c.external_id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(filters.query.trim().toLowerCase())) return false;
      }
      if (filters.owner && c.owner_user_id !== filters.owner) return false;
      if (filters.type && c.type !== filters.type) return false;
      if (filters.segment && c.segment !== filters.segment) return false;
      if (filters.needsEmail && c.email) return false;
      if (filters.status && c.status !== filters.status) return false;

      if (filters.followUp) {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
        const endOf7Days = new Date(startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000);

        if (!c.next_follow_up) return false;
        const followUp = new Date(c.next_follow_up);

        if (filters.followUp === 'overdue' && followUp >= startOfToday) return false;
        if (filters.followUp === 'today' && (followUp < startOfToday || followUp >= endOfToday)) return false;
        if (filters.followUp === 'next7days' && (followUp < startOfToday || followUp >= endOf7Days)) return false;
      }

      return true;
    });
  }, [contacts, filters]);

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setDrawerOpen(true);
  };

  const handleCreate = () => {
    setEditingContact(null);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingContact(null);
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads</h1>
          <p className="text-slate-400 text-sm mt-1">
            {filteredContacts.length} of {contacts.length} leads
            {isAdmin ? ' · all teams' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Add Lead
          </button>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800">
        <div className="p-4 border-b border-slate-800">
          <LeadFilters filters={filters} onChange={setFilters} members={members} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-orange-500" />
          </div>
        ) : (
          <LeadTable contacts={filteredContacts} members={members} onEdit={handleEdit} onChanged={fetchData} />
        )}
      </div>

      {drawerOpen && (
        <LeadDrawer
          contact={editingContact}
          team={team}
          members={members}
          userId={userId}
          onClose={handleDrawerClose}
          onSaved={fetchData}
        />
      )}
    </div>
  );
};

export default LeadsPage;
